import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import '../Box/index.js';
import { withBox } from '../Box/withBox.js';

const getPadding = ({ small, hasIcon, }) => {
    const borderPadding = {
        small: '7px 16px',
        smallWithIcon: '10px 12px 10px 42px',
        regular: '10px 12px',
        regularWithIcon: '10px 12px 10px 42px',
    };
    if (small) {
        return hasIcon ? borderPadding.smallWithIcon : borderPadding.small;
    }
    return hasIcon ? borderPadding.regularWithIcon : borderPadding.regular;
};
const inputBorderStyle = css `
  ${({ small, disabled, invalid, theme, hasIcon }) => css `
    width: 100%;
    padding: ${getPadding({ small, hasIcon })};

    border: 1px solid ${theme.colors.border.B};
    border-radius: 6px;
    outline-offset: unset;

    transition: border-color ${theme.transitions.duration[1]}
        ${theme.transitions.timing.easeInOut},
      padding ${theme.transitions.duration[4]}
        ${theme.transitions.timing.easeOutExponential};

    ::placeholder {
      color: ${theme.colors.foreground.secondary.D};
      opacity: 1; /* Override Firefox default */
    }

    ::selection {
      background-color: ${invalid
    ? theme.colors.state.negative.E
    : theme.colors.sketch.E};
    }

    /* Add the disabled look and feel to the stripe inputs */
    ${disabled &&
    css `
      color: ${theme.colors.foreground.secondary.D};
      background-color: ${theme.colors.background.secondary.B};

      border: 1px dashed ${theme.colors.border.A};
    `};

    ${invalid &&
    css `
      border: 2px solid ${theme.colors.state.negative.A};
    `};

    &.StripeElement--focus {
      ${invalid &&
    css `
        border: none;
      `};
    }

    &.StripeElement--invalid {
      ${css `
        border: 2px solid ${theme.colors.state.negative.A};
      `};
    }
  `}
`;
const inputBaseStyles = css `
  ${({ small, disabled, stripeFakeFocus, theme }) => css `
    display: block;
    position: relative;

    height: ${small ? `38px` : `40px`};
    width: 100%;
    padding: ${small ? `8px` : `10px 12px;`};

    background-color: ${theme.colors.background.secondary.B};
    color: ${theme.colors.foreground.secondary.A};
    appearance: none;

    font-size: ${theme.fontSizes.E};

    ${!disabled &&
    css `
      box-shadow: ${theme.shadows.boxShadow.inner};
    `}

    ${stripeFakeFocus &&
    css `
      background-color: ${theme.colors.background.secondary.A};
    `}

    &:disabled {
      color: ${theme.colors.foreground.secondary.D};
      background-color: ${theme.colors.background.secondary.B};
    }

    /**
     * Work-around for the border animation noted in the issue
     * https://github.com/sketch-hq/Cloud/issues/595.
     *
     * Because the initial border color is "black" when adding style,
     * do to "transition: border-color 0.15s ease-in-out;" the color will animate
     * to the final color.
     *
     * making sure that the color is already the correct one before appending other classes
     * prevents that.
     */
    border-color: ${theme.colors.border.B};

    &[type='text'],
    &[type='email'],
    &[type='url'],
    &[type='password'],
    &[type='search'],
    &[type='button'],
    &[type='number'],
    &.StripeElement {
      ${inputBorderStyle}
    }
  `}
`;
const InputWithBox = styled(withBox('input')) `
  ${inputBaseStyles}
`;
const fadeInOut = keyframes `
  0% { opacity: 0; }
  100% { opacity: 1; }
`;
const Wrapper = styled.div `
  position: relative;
  width: 100%;
`;
const IconWrapper = styled.div `
  position: absolute;
  top: 50%;
  left: 12px;
  display: flex;
  transform: translateY(-50%);
  z-index: 1;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  opacity: 1;

  animation: ${fadeInOut} ${({ theme }) => theme.transitions.duration[2]};
`;
const InputBase = React.forwardRef(function StyledInput(props, ref) {
    const { icon: Icon, value } = props, remainingProps = __rest(props, ["icon", "value"]);
    const isValueEmpty = !(value === null || value === void 0 ? void 0 : value.toString().length);
    const hasIcon = isValueEmpty && !!Icon;
    return (jsxs(Wrapper, { children: [hasIcon && jsx(IconWrapper, { children: Icon }), jsx(InputWithBox, Object.assign({ ref: ref, hasIcon: hasIcon, value: value }, remainingProps))] }));
});
const Input = styled(InputBase) ``;

export { Input, inputBaseStyles, inputBorderStyle };
